import { Link } from "@tanstack/react-router";

import { Card, CardContent } from "@/components/ui/card";

import { PageJump } from "../kit/page-jump";
import { type PlanColumn, PlanTable } from "../kit/plan-table";
import {
	OBJECT_LAYERS,
	PLAN_OBJECTS,
	type PlanObject,
	planPage,
	resolvePage,
} from "../page-registry";
import { BlockTitle } from "./shared";

/**
 * 链路总览第二个页签：维护对象（问题与主张之后）。回答「我要改一个东西，该去哪改」：
 * 行＝承载对象，列＝三层，每格写这一层放什么（照基线 02 三之一，数据在页面注册表 objects），点格子打开对象的维护页。
 * 事实、配置不分层，不在这里；Skill 还在方案阶段，虚线、不可点。
 */

/** 「不放」开头的格子是明确不放，不用跳 */
const isNone = (t: string) => t.startsWith("不放") && !t.includes("，");

/** 这一格的取值放在「业务参数」页（退货窗口、店铺参数、平台口径、币种都在那里）：格子文字后补一个小链接，不加行 */
const BIZ_PARAM_CELLS = new Set(["rules:L2", "knowledge:L3"]);

function Cell({ o, layer, text }: { o: PlanObject; layer: string; text: string }) {
	const r = o.page && !o.planned ? resolvePage(o.page) : null;
	if (isNone(text) || !r || !r.tab) {
		return <span className="text-text-muted">{text}</span>;
	}
	return (
		<>
			<Link
				to="/plan-lab"
				hash={r.tab}
				title={`打开「${planPage(r.tab).name}」页`}
				className="text-text-primary underline decoration-dotted decoration-text-muted underline-offset-4 hover:text-brand-accent"
				data-testid={`plan-objects-cell-${o.key}`}
			>
				{text}
			</Link>
			{BIZ_PARAM_CELLS.has(`${o.key}:${layer}`) ? (
				<>
					{" · 取值见 "}
					<PageJump to="business-params" />
				</>
			) : null}
		</>
	);
}

const ROWS = PLAN_OBJECTS.filter((o) => o.layers);

const COLUMNS: PlanColumn<PlanObject>[] = [
	{
		key: "name",
		title: "对象",
		className: "w-28",
		render: (o) =>
			o.planned ? (
				<span className="inline-flex items-center gap-1 text-text-muted">
					{o.label}
					<span className="text-xs">方案阶段</span>
				</span>
			) : (
				<span className="font-medium text-text-primary">{o.label}</span>
			),
	},
	...OBJECT_LAYERS.map(
		(l): PlanColumn<PlanObject> => ({
			key: l.key,
			title: <span title={l.test}>{l.label}</span>,
			className: "min-w-[14rem] whitespace-normal py-2 text-xs leading-5",
			render: (o) => <Cell o={o} layer={l.key} text={o.layers?.[l.key] ?? ""} />,
		}),
	),
];

export function MaintainObjects() {
	return (
		<Card data-testid="plan-block-objects">
			<CardContent className="space-y-4 p-6">
				<BlockTitle
					title="维护对象"
					sub="我要改一个东西，该去哪改：对象回答放哪类，层回答归谁；点每格打开对应页面"
				/>
				<PlanTable columns={COLUMNS} rows={ROWS} rowKey={(o) => o.key} />
			</CardContent>
		</Card>
	);
}
